

import React from 'react';
import ContinueIcon from '@mui/icons-material/PlayArrow';
import FlightTakeoffIcon from '@mui/icons-material/FlightTakeoff';
import DoNotDisturbAltIcon from '@mui/icons-material/DoNotDisturbAlt';
import { ArcButton, ArcStack, ArcText } from '@src/core/ui';
import { EmergencyStatusEnum } from '@src/features/order/entities';
import { toast } from 'react-toastify';
import { ArcSocketContext } from '@src/core/socket';
import { useConfirm } from '@src/core/controllers/confirm-controller/hooks';
import PauseIcon from '@mui/icons-material/Pause';
import ReplayRoundedIcon from '@mui/icons-material/ReplayRounded';
import { EmergencyButtonsProps } from './types';
import ToggleCollapseView from '@src/features/drone/components/toggle-collapse-view';
import { useSendCommandMutation as useSendCommandMutationForTakeOff } from '@src/features/drone/redux';
import { useFlightProvider } from '@src/features/order/provider';
import { useGoHereOptional } from '@src/features/drone/context/go-here-context';
import {
  clearActiveGoHere,
  shouldClearGoHereOnEmergencyCommand,
} from '@src/features/drone/utils/go-here-registry';
import { useMissionRouteClearance } from '@src/features/drone/hooks/useMissionRouteClearance';


const EmergencyButtons: React.FC<EmergencyButtonsProps> = (props) => {
  const { drone, isLoading } = props;

  const { flightMode } = useFlightProvider();
  const { arcSocket } = React.useContext(ArcSocketContext);
  const goHere = useGoHereOptional();
  // clearMissionRoute() runs optimistically on click; the hook also subscribes to the
  // END_MISSION COMMAND_STATUS SUCCESS event as a backup. endedMissionSerials keeps the route
  // suppressed (and the drone idle) until REST confirms IDLE, so no stale route can revive.
  const { clearMissionRoute } = useMissionRouteClearance(drone);
  const [ sendCommandForTakeOff ] = useSendCommandMutationForTakeOff();

  const dismissGoHere = React.useCallback(() => {
    goHere?.resetGoHere();
    clearActiveGoHere(drone?.serialNumber);
  }, [goHere, drone?.serialNumber]);

  const { confirm } = useConfirm();

  const onSend = async (command: EmergencyStatusEnum) => {
    // Show confirmation for LAND command
    if (command === EmergencyStatusEnum.LAND) {
      const confirmed = await confirm(
        'The drone will descend and land at its current location.',
        'Confirm Land?'
      );
      if (!confirmed) {
        return;
      }
    }
    
    // Show confirmation for RETURN command
    if (command === EmergencyStatusEnum.RETURN) {
      const confirmed = await confirm(
        'The drone will stop its mission and fly back to the return point.',
        'Confirm Return?'
      );
      if (!confirmed) {
        return;
      }
    }
    
    // Show confirmation for PAUSE command
    if (command === EmergencyStatusEnum.PAUSE) {
      const confirmed = await confirm(
        'The drone will stop and hover in place until further command.',
        'Confirm Pause?'
      );
      if (!confirmed) {
        return;
      }
    }

    if (command === EmergencyStatusEnum.CONTINUE) {
      const confirmed = await confirm(
        'The drone will continue its flight from the current position.',
        'Continue Flight?'
      );
      if (!confirmed) {
        return;
      }
    }


    if(!drone) {
      toast.error('Drone is not selected');
      return;
    }

    if (shouldClearGoHereOnEmergencyCommand(command)) {
      dismissGoHere();
    }
    sendCommandForTakeOff({ droneId: drone?.id.toString(), command }).then(() => {
      toast.success(`${command} SUCCESS TEXT`);
    });
  };

  const onEndMission = async () => {
    const confirmed = await confirm(
      'The drone will disarm, save flight data, and finish current mission. Ensure it has safely landed and the area around it is clear.',
      'Confirm End Mission?'
    );
    if (!confirmed) return;

    dismissGoHere();
    const socket = arcSocket();
    if (socket && socket.readyState === WebSocket.OPEN) {
      socket.send(
        JSON.stringify({
          type: 'COMMAND',
          command: 'END_MISSION',
          serialNumber: drone?.serialNumber,
        })
      );
      // Command sent: optimistically clear the route now. Suppression (endedMissionSerials) holds
      // the idle view until REST confirms IDLE, so the route can't revive from lagging data.
      clearMissionRoute();
    }
  };

  return ( 
    <ArcStack>
      <ToggleCollapseView label='EMERGENCY CONTROLS'>

        {isLoading ?  <ArcStack>
          <ArcText.h5 color="text.secondary" fontSize={12}>Loading...</ArcText.h5>
        </ArcStack> :  (
          <ArcStack spacing={1} marginTop={1}>
            <ArcStack direction='row' spacing={1}>
              <ArcButton.ghost 
                fullWidth 
                onClick={() => onSend(EmergencyStatusEnum.LAND)}
                startIcon={<FlightTakeoffIcon />}> 
              LAND 
              </ArcButton.ghost>
              <ArcButton.ghost 
                fullWidth 
                onClick={() => onSend(EmergencyStatusEnum.RETURN)}
                startIcon={<ReplayRoundedIcon />}> 
              RETURN 
              </ArcButton.ghost>
              {flightMode?.value === 'HOLD' 
                ? <ArcButton.ghost 
                  fullWidth 
                  isLoading={isLoading}
                  startIcon={<ContinueIcon />}
                  onClick={() => onSend(EmergencyStatusEnum.CONTINUE)}> 
                CONTINUE 
                </ArcButton.ghost> 
                : <ArcButton.ghost 
                  fullWidth 
                  isLoading={isLoading}
                  startIcon={<PauseIcon />}
                  onClick={() => onSend(EmergencyStatusEnum.PAUSE)}> 
                PAUSE
                </ArcButton.ghost> }
            </ArcStack>
            <ArcStack>
              <ArcButton.ghost 
                fullWidth 
                onClick={onEndMission}
                startIcon={<DoNotDisturbAltIcon />}> 
              END MISSION 
              </ArcButton.ghost>
            </ArcStack>
          </ArcStack>
        ) }
      </ToggleCollapseView>
    </ArcStack>
  );
};

export default EmergencyButtons;